import { paginationSearchFields, sortSearchFields } from "@/lib/search";
import i18n from "i18next";
import { z } from "zod";

/**
 * Shared types + constants for the `/time-entries` list page and its
 * row / filter components (see ./). The page owns the mutable state;
 * these are the data shapes the loader returns and the badges render.
 */

export const SORT_KEYS = [
  "spentOn",
  "user",
  "project",
  "workItem",
  "hours",
  "source",
  "approval",
  "billing",
] as const;
export type SortKey = (typeof SORT_KEYS)[number];

// All list-shaped filters carry the same URL grammar (`a,b,c` for
// include, `!a,b` for exclude — parsed by `parseListFilter`). Keep
// every entry as `z.string().optional()` so a bookmarked link with
// the new grammar still round-trips through the router.
export const timeEntriesSearchSchema = z.object({
  q: z.string().optional(),
  userId: z.string().optional(),
  projectId: z.string().optional(),
  workItemId: z.string().optional(),
  overhead: z.enum(["only", "none"]).optional(),
  approval: z.string().optional(),
  billing: z.string().optional(),
  tag: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  ...sortSearchFields(SORT_KEYS),
  ...paginationSearchFields,
  showDiscarded: z.boolean().optional(),
});

export type TimeEntriesSearch = z.infer<typeof timeEntriesSearchSchema>;

// `fixed_price` is auto-stamped on TEs bound to a fixed-price WI and is
// read-only (never set via the bulk dropdown) — so it's part of the row
// type, the badge, and the filter, but not `BulkBillingState`.
export type BillingState =
  | "unbilled"
  | "to_invoice"
  | "invoiced"
  | "paid"
  | "non_billable"
  | "fixed_price";
export type BulkBillingState = "unbilled" | "to_invoice" | "invoiced" | "paid" | "non_billable";

export type TimeEntry = {
  id: string;
  spentOn: string;
  startedAt: string | null;
  endedAt: string | null;
  hours: string;
  source: string;
  /** Employee-side pipeline. `logged` is the pre-decision bucket;
   *  `budget` is a manager-parked "decide later" lane on top of it.
   *  Manager flips to `approved` or `rejected`. `paid` is the
   *  accountant-only payout state on top of `approved`. */
  approvalState: "worked" | "budget" | "approved" | "paid" | "rejected";
  billingState: BillingState;
  description: string | null;
  /** Set on Clockify-imported rows when the sync extracted a `#<n>`
   *  reference from the description or task name. Used by the triage
   *  UI as a hint when offering a WI picker. Null for everything else. */
  detectedRef: string | null;
  billable: boolean;
  /** Null when the Clockify entry's owner has no matching Hub user
   *  (external contributor / shared workspace). `externalUserEmail`
   *  then carries the Clockify-side identity. */
  user: { id: string; name: string | null } | null;
  /** Clockify-side owner email, set only when `user` is null. */
  externalUserEmail: string | null;
  /** Nullable since the 2026-05-20 unification — a `logged` Clockify
   *  TE whose project isn't bound to Hub lands here with no project
   *  until the user triages. Post-triage rows always have one. */
  project: {
    id: string;
    name: string;
    slug: string;
    group: { path: string } | null;
    /** Customer the project bills to — drives the bulk "report" bar's
     *  same-customer gate. Null only on projects with no customer. */
    customer: { id: string; name: string } | null;
  } | null;
  /** Clockify-side project name, set only when `project` is null but
   *  the Clockify entry did carry a (non-Hub) project. */
  externalProjectName: string | null;
  workItem: { id: string; title: string; gitlabIssueId: string; globalId: number } | null;
  /** Per-project overhead category the row binds to instead of a WI.
   *  Mutually exclusive with `workItem` — exactly one of the two is
   *  set on a "decided" row (logged Clockify imports have both null
   *  until triage). Listing renders the overhead name in place of
   *  the work-item slot. */
  overhead: { id: string; name: string; isDefault: boolean } | null;
  /** Clockify tag names mirrored 1:1 from the source entry. Display-only
   *  for everyone but admin, who can edit them via the per-row pencil
   *  (writes through to Clockify). Empty for manual / untagged rows. */
  tags: string[];
  /** Non-null when the row is attached to a Fakturoid invoice line —
   *  once invoiced + linked, neither the employee (approval) nor the
   *  customer (billing) state can be changed without first detaching
   *  the line on the Fakturoid side. */
  invoiceItemId: string | null;
  /** The report (InvoiceBatch) this entry is on, resolved through its
   *  invoice item. Non-null exactly when `invoiceItemId` is — drives
   *  the "On report" link in the Billing column. Once set, the entry's
   *  billing state is owned by the report's lifecycle. */
  invoiceItem: { batch: { id: string; status: string } } | null;
};

export type TimeEntriesResponse = {
  entries: TimeEntry[];
  total: number;
  /** Sum of hours across the whole filtered set (not just the
   *  current page) — surfaced next to the entry count in the
   *  toolbar. Decimal string, same shape as `TimeEntry.hours`. */
  totalHours: string;
  page: number;
  pageSize: number;
  users: Array<{ id: string; name: string | null }>;
  projects: Array<{ id: string; name: string }>;
  workItems: Array<{ id: string; label: string }>;
  /** Distinct overhead-category names — feeds the synthetic
   *  `Overhead: <name>` options in the WI filter (Misc /
   *  Communication / custom). */
  overheadFacets: string[];
  /** Distinct Clockify tag names present in the viewer's scope — used
   *  to populate the Tag filter dropdown. */
  tagFacets: string[];
  /** Clockify users (emails) present in scope but not mirrored to
   *  Hub. The user filter offers them under `email:<addr>` so admins
   *  can target orphan rows. Empty for non-admins / accountants. */
  externalUsers: string[];
  /** Clockify project names present in scope but not mirrored to
   *  Hub. The project filter offers them under `name:<projectName>`. */
  externalProjects: string[];
  canSeeAll: boolean;
  /** Hub project ids the viewer manages — lets a manager (who isn't
   *  admin / accountant) trigger Approve / Budget / Reject on rows
   *  whose project they manage. Empty for everyone else. */
  managedProjectIds: string[];
  /** Full Hub project list — feeds the inline Project picker on the
   *  viewer's own `worked` rows. */
  allProjects: Array<{ id: string; name: string }>;
  /** Open work items keyed by project id — feeds the inline WI picker
   *  on both `logged` (triage) and the viewer's own `worked` (rebind)
   *  rows. Only populated for projects the viewer has at least one
   *  editable row in, so the payload stays bounded. */
  workItemsByProject: Record<string, Array<{ id: string; label: string; closed: boolean }>>;
  /** Per-project `canMarkOverhead` flag for the triage UI on logged
   *  rows. Empty for non-staff viewers (server only computes for
   *  projects with at least one logged row on the page). */
  overheadByProject: Record<string, boolean>;
  /** The viewer's own user id — used to decide which rows expose the
   *  inline project / WI / overhead pickers. */
  viewerId: string;
};

export type InboxRow = {
  id: string;
  description: string | null;
  hours: string;
  spentOn: string;
  startedAt: string | null;
  endedAt: string | null;
  billable: boolean;
  detectedRef: string | null;
  user: { id: string; name: string | null };
  /** Nullable since the 2026-05-20 inbox→TimeEntry unification — a
   *  Clockify entry whose project isn't mapped to Hub still gets a
   *  `logged` row, but with no Hub project yet. */
  project: { id: string; name: string } | null;
  canMarkOverhead: boolean;
  workItems: Array<{ id: string; label: string; closed: boolean }>;
};

/** The main table's tbody mixes two row kinds: read-only discarded
 *  triage rows (rendered by `InboxTableRow` via DataTable's renderRow)
 *  pinned first, then the regular logged entries. */
export type MainRow = { kind: "entry"; te: TimeEntry } | { kind: "discarded"; row: InboxRow };

export type InboxResponse = {
  rows: InboxRow[];
  total: number;
  /** Full Hub-project list — only populated when at least one row on
   *  the page has a null project, so the triage UI can show a
   *  project picker on it. */
  allProjects?: Array<{ id: string; name: string }>;
};

export type LoaderData = {
  entries: TimeEntriesResponse;
  inbox: InboxResponse;
  /** Discarded inbox rows — only fetched when the toggle is on. */
  discarded: InboxResponse;
};

/** The per-row gate fields the bulk bars filter on — projected from a
 *  visible `TimeEntry`, or fetched via "select all matching" for
 *  off-page rows. */
export type SelectionRow = {
  id: string;
  approvalState: TimeEntry["approvalState"];
  billingState: BillingState;
  projectId: string | null;
  userId: string | null;
  invoiceItemId: string | null;
  /** The day the row is filed under, and when its interval stopped —
   *  what "has this day closed?" is decided from. Approving is a day in
   *  arrears, so the bar only offers it for rows whose day is over. */
  spentOn: string;
  endedAt: string | null;
  /** Customer the row bills to (via its project) — the bulk "report"
   *  bar only shows when every selected row shares one. */
  customerId: string | null;
};

export type ApprovalStateValue = "worked" | "budget" | "approved" | "paid" | "rejected";

export type BulkAction =
  | {
      action:
        | "approve"
        | "budget"
        | "reject"
        | "mark_paid"
        | "unmark_paid"
        | "mark_overhead"
        | "discard";
    }
  | { action: "set_billing"; billingState: BulkBillingState }
  | { action: "set_approval"; approvalState: ApprovalStateValue }
  | { action: "bind_project"; projectId: string }
  | { action: "bind_work_item"; workItemId: string }
  | { action: "add_tags"; addTags: string[] }
  | { action: "remove_tags"; removeTags: string[] }
  | { action: "reassign_user"; userId: string };

export const approvalVariant: Record<
  string,
  "default" | "secondary" | "success" | "outline" | "destructive" | "warning"
> = {
  logged: "secondary",
  budget: "warning",
  approved: "success",
  paid: "outline",
  rejected: "destructive",
};

export const billingVariant: Record<
  string,
  "default" | "secondary" | "success" | "warning" | "outline"
> = {
  unbilled: "secondary",
  to_invoice: "warning",
  invoiced: "success",
  paid: "success",
  non_billable: "outline",
  fixed_price: "default",
};

// Display labels — `paid` means different things on each axis, so
// spell them out to avoid the "two paids" confusion: employee-side
// `paid` = the worker was paid out; customer-side `paid` = the
// invoice was paid by the customer. Resolved at call time so a language
// switch is picked up; an unknown state falls back to its raw value.
export function approvalLabel(state: string): string {
  return i18n.t(`timeEntries.approval.${state}`, { defaultValue: state });
}
export function billingLabel(state: string): string {
  return i18n.t(`timeEntries.billing.${state}`, { defaultValue: state });
}

/** `GET /api/time-entries/summary` — mirrors `TimeEntrySummary` on the server. */
export type TimeEntrySummary = {
  totalHours: number;
  count: number;
  days: number;
  byProject: Array<{ projectId: string | null; name: string | null; hours: number }>;
  projects: Array<{ id: string; name: string }>;
};
